import { useCallback, useMemo } from 'react';
import { PaymentRequestSettingFormType } from './type';

import { isSuccess } from '@/context/services/type';
import { useQueryClient } from '@tanstack/react-query';
import {
  ApOfficeMemberReceivedInvoiceNotificationSetting,
  ApOfficeMemberReceivedInvoiceNotificationSettingFrequency,
  CommonError,
  useGetApOfficeMemberReceivedInvoiceNotificationSettingSuspense,
  useUpdateApOfficeMemberReceivedInvoiceNotificationSetting,
  ApOfficeMemberReportNotificationSetting,
  useGetApOfficeMemberReportNotificationSettingSuspense,
  useUpdateApOfficeMemberReportNotificationSetting,
} from 'ap-openapi';

export type ReportNotificationSetting = ApOfficeMemberReportNotificationSetting;

export const useNotificationSettingInfoSuspense = () => {
  const { data, error, refetch } =
    useGetApOfficeMemberReportNotificationSettingSuspense();
  if (error) throw error;
  if (!data.data) throw Error;
  const {
    approvable_report: approvableReport,
    workflow,
    report_timeline: reportTimeline,
    report_viewer: reportViewer,
  } = data.data;

  return useMemo(
    () => ({
      data: {
        approvableReport: approvableReport.is_active,
        workflow: workflowEnum(workflow),
        reportTimeline: reportTimeline.is_active,
        reportViewer: reportViewer?.is_active,
      },
      refetch: refetch,
    }),
    [approvableReport, workflow, reportTimeline, reportViewer, refetch]
  );
};

type TUpdateReporteNotificationMutateArgs = ReturnType<
  typeof useUpdateApOfficeMemberReportNotificationSetting
>['mutateAsync'];
type UpdateReporteNotificationMutateArgs =
  Parameters<TUpdateReporteNotificationMutateArgs>['0'];
export const useUpdateReportNotificationSetting = () => {
  const query = useQueryClient();
  const { mutateAsync: _mutateAsync } =
    useUpdateApOfficeMemberReportNotificationSetting({
      mutation: {
        onSuccess: () => {
          query.invalidateQueries({
            queryKey: ['/api/js/ap_office_member_report_notification_setting'],
          });
        },
      },
    });
  const mutateAsync = useCallback(
    async (args: UpdateReporteNotificationMutateArgs) => {
      const resp = await _mutateAsync(args);
      if (isSuccess(resp.status)) {
        return {
          status: 'success',
        };
      } else {
        return {
          status: 'error',
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ?? {}) as CommonError,
        };
      }
    },
    [_mutateAsync]
  );
  return { mutateAsync };
};

const workflowEnum = (
  workflow: ReportNotificationSetting['workflow']
): PaymentRequestSettingFormType['workflow'] => {
  if (!workflow.is_active) return 'not_notify';
  if (workflow.only_final_approval) return 'only_final_approval';
  return 'every_step';
};

export type ReceivedInvoiceNotificationSetting =
  ApOfficeMemberReceivedInvoiceNotificationSetting;
export type ReceivedInvoiceNotificationSettingFrequency =
  ApOfficeMemberReceivedInvoiceNotificationSettingFrequency;

export const useGetApOfficeMemberReceivedInvoiceNotificationSetting = () => {
  const { data, error, isLoading } =
    useGetApOfficeMemberReceivedInvoiceNotificationSettingSuspense();
  if (error) throw error;
  return useMemo(
    () => ({
      data: data?.data,
      isLoading,
    }),
    [data, isLoading]
  );
};

type TUpdateMemberReceivedInvoiceNotificationArgs = ReturnType<
  typeof useUpdateApOfficeMemberReceivedInvoiceNotificationSetting
>['mutateAsync'];
type UpdateReceivedInvoiceNotificationMutateArgs =
  Parameters<TUpdateMemberReceivedInvoiceNotificationArgs>['0'];
export const useUpdateReceivedInvoiceNotificationSetting = () => {
  const query = useQueryClient();
  const { mutateAsync: _mutateAsync } =
    useUpdateApOfficeMemberReceivedInvoiceNotificationSetting({
      mutation: {
        onSuccess: () => {
          query.invalidateQueries({
            queryKey: [
              '/api/js/ap_office_member_received_invoice_notification_setting',
            ],
          });
        },
      },
    });
  const mutateAsync = useCallback(
    async (args: UpdateReceivedInvoiceNotificationMutateArgs) => {
      const resp = await _mutateAsync(args);
      if (isSuccess(resp.status)) {
        return {
          status: 'success',
        };
      } else {
        return {
          status: 'error',
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ?? {}) as CommonError,
        };
      }
    },
    [_mutateAsync]
  );
  return { mutateAsync };
};
